import isEntityInformationTable from 'types/visTypeCheckers/isEntityInformationTable';
import isGroupVsRemainder from 'types/visTypeCheckers/isGroupVsRemainder';
import isRemindersGadget from 'types/visTypeCheckers/isRemindersGadget';

import {
  getEntityDetailsDataTypes,
  guessEntityDetailsCard,
  isEntityDetailsTable,
  reproducesEntityInformationTable,
} from './buildEntityInformationTableDefinition';
import {
  guessGroupVsRemainderCard,
  isGroupVsRemainderLine,
  reproducesGroupVsRemainder,
} from './buildGroupVsRemainderDefinition';
import {
  guessRemindersCard,
  isRemindersTable,
  reproducesReminders,
} from './buildRemindersDefinition';
import { isMetricListTable } from './buildMetricListDefinition';
import { isRecordsTable } from './buildTableDefinition';
import { isKpiListTable } from './kpiList';

// The dataset Reminders read (useDashboardGadgetDataTypes)
export const REMINDERS_DATA_TYPE = 'upcomingEvents';

// Legacy gadgets Studio edits in one of its own vis types and saves as they
// are: Reminders and Entity Information Tables as Tables, Group Vs
// Remainder as a Line
export type LegacyGadget =
  | VisualisationDefinitions.Reminders
  | VisualisationDefinitions.GroupVsRemainder
  | VisualisationDefinitions.EntityInformationTable;

type ContentWithModes = Pick<Studio.ChartContent, 'visType' | 'description'> & {
  table?: Studio.TableOptions;
  line?: Studio.LineOptions;
};

export const isLegacyGadget = (
  chartDef: ExtendedVisualisationDefinition | V5ChartDefinition | undefined,
): chartDef is LegacyGadget =>
  isRemindersGadget(chartDef as ExtendedVisualisationDefinition) ||
  isGroupVsRemainder(chartDef as ExtendedVisualisationDefinition) ||
  isEntityInformationTable(chartDef as ExtendedVisualisationDefinition);

// Whether a block saves as one of them, or as a legacy Metric List or
// Scorecard card
export const isLegacyGadgetBlock = (content: ContentWithModes): boolean =>
  isRemindersTable(content) ||
  isEntityDetailsTable(content) ||
  isGroupVsRemainderLine(content) ||
  isMetricListTable(content) ||
  isKpiListTable(content);

// A block's settings from the saved gadget
export const guessLegacyGadgetCard = (
  chartDef: LegacyGadget,
): Studio.GadgetSidecarCard =>
  isRemindersGadget(chartDef)
    ? guessRemindersCard(chartDef)
    : isGroupVsRemainder(chartDef)
      ? guessGroupVsRemainderCard(chartDef)
      : guessEntityDetailsCard(chartDef);

// Whether the settings rebuild the saved gadget
export const reproducesLegacyGadget = ({
  card,
  chartDef,
  isReportGadget,
}: {
  card: Studio.GadgetSidecarCard;
  chartDef: LegacyGadget;
  isReportGadget: boolean;
}): boolean =>
  isRemindersGadget(chartDef)
    ? reproducesReminders({ card, chartDef })
    : isGroupVsRemainder(chartDef)
      ? reproducesGroupVsRemainder({ card, chartDef })
      : reproducesEntityInformationTable({ card, chartDef, isReportGadget });

// Blocks that list or draw something other than their metrics: a dataset's
// records, reminders, an entity's details (whose metrics are its rows'), a
// field against the rest or a KPI List (whose metrics are its KPIs'). Dropped metrics don't go into them, and they
// aren't empty without metrics.
export const takesNoMetrics = (content: ContentWithModes): boolean =>
  isRecordsTable(content) || isLegacyGadgetBlock(content);

// The datasets a block without metrics reads, for its date field and
// filters
export const getMetricFreeDataTypes = (content: ContentWithModes): string[] => {
  if (isRecordsTable(content)) {
    const dataType = content.table?.records?.dataType;
    return dataType ? [dataType] : [];
  }
  if (isRemindersTable(content)) {
    return [REMINDERS_DATA_TYPE];
  }
  if (isGroupVsRemainderLine(content)) {
    const dataset = content.line?.groupVsRemainder?.dataset;
    return dataset ? [dataset] : [];
  }
  if (isKpiListTable(content)) {
    return [];
  }
  return getEntityDetailsDataTypes(content);
};

// What a block saved as a legacy gadget is called until it's named
export const getLegacyGadgetBlockName = (
  content: ContentWithModes,
): string | undefined => {
  if (isRemindersTable(content)) {
    return 'Reminders';
  }
  if (isEntityDetailsTable(content)) {
    return 'Entity details';
  }
  if (isGroupVsRemainderLine(content)) {
    return 'Group vs remainder';
  }
  if (isMetricListTable(content)) {
    return 'Metric list';
  }
  if (isKpiListTable(content)) {
    return 'KPI list';
  }
  return undefined;
};
